import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LayoutDashboard, Briefcase, Settings, LogOut, Scale, ChevronRight } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import styles from './Sidebar.module.css';

interface MenuItem {
    name: string;
    path: string;
    icon: React.ElementType;
}

const menuItems: MenuItem[] = [
    { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
    { name: "Gestión de Casos", path: "/casos", icon: Briefcase },
    { name: "Reportes", path: "/reportes", icon: Scale },
    { name: "Configuración", path: "/config", icon: Settings },
];

const navLinkVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i: number) => ({
        opacity: 1,
        x: 0,
        transition: {
            delay: 0.5 + i * 0.1,
            type: 'spring',
            stiffness: 150,
            damping: 20
        }
    })
};

export const Sidebar: React.FC = () => {
    const { isOpen, toggleSidebar } = useSidebar();
    const { user, logout: contextLogout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const handleLogout = () => {
        contextLogout();
        navigate('/login');
    };

    const isLinkActive = (path: string) => {
        return location.pathname.startsWith(path);
    };

    return (
        <aside className={`${styles.sidebar} ${isOpen ? styles.open : styles.closed}`}>
            <div className={styles.sidebarContent}>
                <header className={styles.header}>
                    <motion.div
                        className={styles.logoContainer}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring' }}
                    >
                        <Scale className={styles.logoIcon} />
                        <motion.div
                            initial={false}
                            animate={{ opacity: isOpen ? 1 : 0, width: isOpen ? 'auto' : 0, marginLeft: isOpen ? '12px' : 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <span className={styles.logoText}>LegalSys</span>
                        </motion.div>
                    </motion.div>
                </header>
                <button onClick={toggleSidebar} className={styles.toggleButton} title={isOpen ? "Contraer" : "Expandir"}>
                    <ChevronRight />
                </button>

                <nav className={styles.navMenu}>
                    {menuItems.map((item, index) => (
                        <motion.div
                            key={item.path}
                            custom={index}
                            variants={navLinkVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <Link
                                to={item.path}
                                className={`${styles.navLink} ${isLinkActive(item.path) ? styles.active : ""}`}
                                title={isOpen ? item.name : ''}
                            >
                                <item.icon size={22} className={styles.navIcon} />
                                <span className={styles.navText}>{item.name}</span>
                            </Link>
                        </motion.div>
                    ))}
                </nav>

                <footer className={styles.footer}>
                    <div className={styles.separator} />
                    <button onClick={handleLogout} className={styles.navLink} title="Cerrar Sesión">
                        <LogOut size={22} className={styles.navIcon} />
                        <span className={styles.navText}>Cerrar Sesión</span>
                    </button>
                    <div className={styles.userProfile} title={user?.nombre_completo || 'Usuario'}>
                        <img
                            src={`https://ui-avatars.com/api/?name=${user?.nombre_completo.replace(' ', '+') || 'U'}&background=3d5af1&color=fff&bold=true&size=128`}
                            alt="Avatar"
                            className={styles.userAvatar}
                        />
                        <motion.div
                            initial={false}
                            animate={{ opacity: isOpen ? 1 : 0, width: isOpen ? 'auto' : '0' }}
                            transition={{ duration: 0.2 }}
                            className={styles.userInfo}
                        >
                            <span className={styles.userName}>{user?.nombre_completo}</span>
                            <span className={styles.userRole}>Abogado Principal</span>
                        </motion.div>
                    </div>
                </footer>
            </div>
        </aside>
    );
};

export default Sidebar;